import { formatDistanceToNow } from "date-fns";
import { Archive, ArchiveRestore, ChevronRight, MoreVertical, Pencil, RotateCcw, Trash2, CheckCircle2 } from "lucide-react";
import { PollParticipantSummary } from "./poll-participant-summary";
import { useSafeNavigate } from "../../hooks/useSafeNavigate";
import { useProfileInteraction } from "../../hooks/useProfileInteraction";
import { useAuth } from "../../app/useAuth";
import { ProfileInteractionModals } from "../ProfileInteractionModals";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

function formatDeadlineLabel(deadlineAt, isOpen) {
  if (!deadlineAt) return isOpen ? "No deadline" : "Closed";
  if (!isOpen) return `Closed ${formatDistanceToNow(deadlineAt, { addSuffix: true })}`;
  if (deadlineAt.getTime() <= Date.now()) return "Deadline passed";
  return `Deadline ${formatDistanceToNow(deadlineAt, { addSuffix: true })}`;
}

function voteTypeLabel(voteType) {
  return voteType === "RANKED_CHOICE" ? "Ranked choice" : "Multiple choice";
}

const STATE_STYLE = {
  NEEDS_VOTE:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200",
  OPEN_VOTED:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/70 dark:bg-emerald-900/30 dark:text-emerald-200",
  CLOSED:
    "border-border bg-muted text-muted-foreground",
  ARCHIVED:
    "border-border bg-muted text-muted-foreground opacity-80",
};

const STATE_LABEL = {
  NEEDS_VOTE: "Needs vote",
  OPEN_VOTED: "Voted",
  CLOSED: "Closed",
  ARCHIVED: "Archived",
};

function parentLabel(parentType) {
  return parentType === "scheduler" ? "Session" : "Group";
}

export function BasicPollCard({
  poll,
  onArchiveToggle,
  archiveBusy = false,
  onOpen,
  onReopenPoll,
  onFinalizePoll,
  onEditPoll,
  onDeletePoll,
  canManage = false,
  actionBusy = false,
  showActions = true,
}) {
  const safeNavigate = useSafeNavigate();
  const {
    title,
    contextLabel,
    voteType,
    required,
    deadlineAt,
    state,
    voteLink,
    isOpen,
    eligibleUsers = [],
    votedUsers = [],
    pendingUsers = [],
    votedCount = 0,
    eligibleCount = 0,
    parentType,
    // Layer-S/render flags from the data layer (design §5): when the votes list
    // read was permission-denied, only the OWN vote doc is known, so (a) the
    // per-person voted/pending STACKS must not render from own-only voterIds
    // (showVoteProgress) AND (b) the voted/pending SPLIT numerator must read "—"
    // rather than a definitive (and self-contradictory "N voted" + "M pending"
    // that sums past the eligible total) split off own-only voterIds
    // (statusKnown). Both default true (full list known); the aggregate eligible
    // COUNT always shows. Mirrors basic-poll-voting-card.jsx.
    showVoteProgress = true,
    statusKnown = true,
    showVoterIdentities = true,
    voteIdentityDisplayMode = "named",
    voteVisibilityHint = null,
    isArchived,
    pollStatus = "OPEN",
    accentColor = null,
  } = poll;
  const { user: currentUser } = useAuth();
  const { handleAvatarClick, modalProps } = useProfileInteraction();
  const avatarOnClick = showVoterIdentities ? handleAvatarClick : undefined;
  const primaryActionLabel = state === "NEEDS_VOTE" ? "Vote" : state === "OPEN_VOTED" ? "Edit vote" : "View results";
  const handleOpen = () => {
    if (typeof onOpen === "function") {
      onOpen();
      return;
    }
    if (!voteLink) return;
    safeNavigate(voteLink, { compareMode: "pathname+search" });
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOpen();
  };

  return (
    <>
    <article
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      aria-label={`${primaryActionLabel}: ${title || "Untitled poll"}`}
      className="group relative flex w-full flex-col gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-left transition-all duration-150 motion-safe:hover:scale-[1.02] hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {accentColor ? (
              <span
                aria-hidden="true"
                title="Group color"
                className="inline-block h-3 w-3 shrink-0 rounded-full border border-border ring-1 ring-black/10 dark:ring-white/10"
                style={{ backgroundColor: accentColor }}
              />
            ) : null}
            <p className="truncate text-sm font-semibold text-card-foreground" title={title || "Untitled poll"}>
              {title || "Untitled poll"}
            </p>
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATE_STYLE[state] || STATE_STYLE.CLOSED}`}
            >
              {STATE_LABEL[state] || STATE_LABEL.CLOSED}
            </span>
            {required ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
                Required
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            <span className="truncate" title={contextLabel}>{contextLabel}</span>
            <span aria-hidden="true" className="text-muted-foreground">
              •
            </span>
            <span>{formatDeadlineLabel(deadlineAt, isOpen)}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {voteTypeLabel(voteType)}
            </span>
            <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {parentLabel(parentType)}
            </span>
          </div>
        </div>
        {showActions ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              // Subtle bordered kebab matching the scheduler voting page (no
              // filled circle). Not natively `disabled` while busy: a native
              // disabled button would suppress the click and could let it fall
              // through to the role="button" card and open the poll. The menu
              // items are individually disabled instead, and this onClick stops
              // propagation so the kebab never opens the card.
              onClick={(event) => event.stopPropagation()}
              aria-disabled={actionBusy}
              className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700"
              aria-label="General poll actions"
              title="General poll actions"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {onArchiveToggle ? (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onArchiveToggle();
                }}
                disabled={archiveBusy || actionBusy}
              >
                {isArchived ? <ArchiveRestore className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
                {isArchived ? "Unarchive" : "Archive"}
              </DropdownMenuItem>
            ) : null}
            {canManage && state === "CLOSED" && pollStatus === "FINALIZED" && onReopenPoll ? (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onReopenPoll();
                }}
                disabled={actionBusy}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Re-open
              </DropdownMenuItem>
            ) : null}
            {canManage && pollStatus === "OPEN" && onFinalizePoll ? (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onFinalizePoll();
                }}
                disabled={actionBusy}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Finalize
              </DropdownMenuItem>
            ) : null}
            {canManage && onEditPoll ? (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onEditPoll();
                }}
                disabled={actionBusy}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            ) : null}
            {canManage && onDeletePoll ? (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onDeletePoll();
                }}
                disabled={actionBusy}
                className="text-rose-600 dark:text-rose-400"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
        ) : null}
      </div>

      <PollParticipantSummary
        eligibleUsers={eligibleUsers}
        votedUsers={votedUsers}
        pendingUsers={pendingUsers}
        eligibleCount={eligibleCount}
        votedCount={votedCount}
        showPending={isOpen}
        showVoteProgress={showVoteProgress}
        statusKnown={statusKnown}
        showVoterIdentities={showVoterIdentities}
        voteIdentityDisplayMode={voteIdentityDisplayMode}
        currentUser={currentUser}
        visibilityHint={voteVisibilityHint}
        onAvatarClick={avatarOnClick}
      />

      {/* Visible CTA affordance. The whole card is the click target
          (role="button"), so this is a NON-interactive visual cue — aria-hidden
          because the card's accessible name already carries the action. */}
      <div className="flex items-center justify-end">
        <span
          aria-hidden="true"
          className="inline-flex items-center gap-1 rounded-full bg-brand-primary px-2 py-0.5 text-[10px] font-semibold text-white"
        >
          {primaryActionLabel}
          <ChevronRight className="h-3 w-3" />
        </span>
      </div>
    </article>
    <ProfileInteractionModals {...modalProps} />
  </>
  );
}
